import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import BaseError from 'utils/base-error';
import { createStrapiMediaUrl } from 'shared';
import { fetchSweetShopArticlesByUids } from '../../api/fetch-sweet-shop-articles-by-uids';
import {
    CartCulinaryStudioArticles,
} from '../types/cart-culinary-studio-article';
import {
    CartDeliveryArticles,
} from '../types/cart-delivery-article';
import { fetchCulinaryStudioArticlesByUids } from '../../api/fetch-culinary-studio-articles-by-uids';
import { fetchDeliveryArticlesByUids } from '../../api/fetch-delivery-articles-by-uids';
import { fetchOutdoorBarArticlesByUids } from '../../api/fetch-outdoor-bar-articles-by-uids';
import { fetchCakeToOrdersByUids } from '../../api/fetch-cake-to-orders-by-uids';
import { CartOutdoorBarArticles } from '../types/cart-outdoor-bar-article';
import { CartSweetShopArticles } from '../types/cart-sweet-shop-article';
import { CartCakeToOrder } from '../types/cart-cake-to-order';

export const CartPageActions = {
    fetchDeliveryArticlesByUids: createAsyncThunk(
        'CartPageActionsFetchDeliveryArticlesByUids',
        async ({
            uids,
        }: {
            uids: string[]
        }) => fetchDeliveryArticlesByUids(uids),
    ),
    fetchCulinaryStudioArticlesByUids: createAsyncThunk(
        'CartPageActionsFetchCulinaryStudioArticlesByUids',
        async ({
            uids,
        }: {
            uids: string[]
        }) => fetchCulinaryStudioArticlesByUids(uids),
    ),
    fetchOutdoorBarArticlesByUids: createAsyncThunk(
        'CartPageActionsFetchOutdoorBarArticlesByUids',
        async ({
            uids,
        }: {
            uids: string[]
        }) => fetchOutdoorBarArticlesByUids(uids),
    ),
    fetchSweetShopArticlesByUids: createAsyncThunk(
        'CartPageActionsFetchSweetShopArticlesByUids',
        async ({
            uids,
        }: {
            uids: string[]
        }) => fetchSweetShopArticlesByUids(uids),
    ),
    fetchCakeToOrdersByUids: createAsyncThunk(
        'CartPageActionsFetchCakeToOrdersByUids',
        async ({
            uids,
        }: {
            uids: string[]
        }) => fetchCakeToOrdersByUids(uids),
    ),
};

export type CartPageState = {
    loaded: boolean
    errors: BaseError[],
    deliveryArticles: CartDeliveryArticles[]
    culinaryStudioArticles: CartCulinaryStudioArticles[]
    outdoorBarArticles: CartOutdoorBarArticles[]
    cakeToOrders: CartCakeToOrder[]
    sweetShopArticles: CartSweetShopArticles[]
}

const initialState: CartPageState = {
    loaded: false,
    errors: [],
    deliveryArticles: [],
    culinaryStudioArticles: [],
    outdoorBarArticles: [],
    cakeToOrders: [],
    sweetShopArticles: [],
};

// eslint-disable-next-line import/prefer-default-export
export const cartPageSlice = createSlice({
    name: 'cartPageSlice',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder.addCase(
            CartPageActions.fetchDeliveryArticlesByUids.fulfilled,
            (state, action) => {
                const response = action.payload;
                if (response instanceof BaseError) {
                    state.errors.push(response);
                } else {
                    state.deliveryArticles = response.data.data.map(({
                        slug,
                        name,
                        price,
                        images,
                        currency,
                    }) => ({
                        uid: slug,
                        name,
                        price,
                        image: images && {
                            url: createStrapiMediaUrl(images[0].url),
                        },
                        currency: currency && {
                            abbreviation: currency.abbreviation,
                            name: currency.name,
                        },
                    }));
                }
            },
        );
        builder.addCase(
            CartPageActions.fetchCulinaryStudioArticlesByUids.fulfilled,
            (state, action) => {
                const response = action.payload;

                if (response instanceof BaseError) {
                    state.errors.push(response);
                } else {
                    state.culinaryStudioArticles = response.data.data.map(({
                        slug,
                        name,
                        images,
                        portions,
                        sets,
                    }) => ({
                        uid: slug,
                        name,
                        portions,
                        sets,
                        image: images && {
                            url: createStrapiMediaUrl(images[0].url),
                        },
                    }));
                }
            },
        );
        builder.addCase(
            CartPageActions.fetchOutdoorBarArticlesByUids.fulfilled,
            (state, action) => {
                const response = action.payload;

                if (response instanceof BaseError) {
                    state.errors.push(response);
                } else {
                    state.outdoorBarArticles = response.data.data.map(({
                        slug,
                        name,
                        images,
                        portions,
                        sets,
                    }) => ({
                        uid: slug,
                        name,
                        portions,
                        sets,
                        image: images && {
                            url: createStrapiMediaUrl(images[0].url),
                        },
                    }));
                }
            },
        );
        builder.addCase(
            CartPageActions.fetchCakeToOrdersByUids.fulfilled,
            (state, action) => {
                const response = action.payload;

                if (response instanceof BaseError) {
                    state.errors.push(response);
                } else {
                    state.cakeToOrders = response.data.data.map(({
                        slug,
                        name,
                        images,
                        portions,
                        sets,
                    }) => ({
                        uid: slug,
                        name,
                        portions,
                        sets,
                        image: images && {
                            url: createStrapiMediaUrl(images[0].url),
                        },
                    }));
                }
            },
        );
        builder.addCase(
            CartPageActions.fetchSweetShopArticlesByUids.fulfilled,
            (state, action) => {
                const response = action.payload;
                if (response instanceof BaseError) {
                    state.errors.push(response);
                } else {
                    state.sweetShopArticles = response.data.data.map(({
                        slug,
                        name,
                        images,
                        price,
                        currency,
                    }) => ({
                        uid: slug,
                        name,
                        image: images && {
                            url: createStrapiMediaUrl(images[0].url),
                        },
                        price,
                        currency: currency && {
                            abbreviation: currency.abbreviation,
                            name: currency.name,
                        },
                    }));
                }
            },
        );
    },
});
